import React, { useState, useEffect, useRef, useCallback } from 'react';
import './styling/SelectDropdown.css';

/*
  SelectDropdown Component (Shared Form Element)
  Purpose: Generic, accessible dropdown for selecting an item from a list with project-wide styling.
  Props:
    - items: Array<{ id: string, name?: string, content?: ReactNode }> // name for accessible label; content optional custom node
    - selectedId: string | null
    - onChange(item): function called when selection changes
    - placeholder: string (shown when no selection)
    - label: string (optional label rendered above control)
    - fullWidth: boolean (stretch to container width)
    - disabled: boolean (non-interactive state)
    - icon: ReactNode (optional leading icon)
    - size: 'small' | 'medium' (control height & font sizing)
    - renderItem?: (item, active:boolean) => ReactNode // custom item renderer
    - renderTrigger?: (selectedItem|null) => ReactNode  // custom trigger content
    - renderItemActions?: (item) => ReactNode // trailing per-row controls (edit/delete...).
        Rendered as a SIBLING of the option button, not inside it — nesting a button
        inside the option button would be invalid HTML and would swallow its click.
        Return null for rows that have no actions. Clicking an action closes the
        list but never selects the row.
    - Grouped lists: an item may carry `group: string`. A sticky, non-selectable
        heading row is inserted wherever the group changes (items keep the order
        given — put each group's items together). Headings are not options: they
        are skipped by keyboard navigation and never fire onChange. Items without
        a group render flat, wherever they sit in the list.
    - renderGroup?: (group: string, groupItems: item[]) => ReactNode // heading content
        (default: the group string).
    - An item may carry `muted: true` to render dimmed (still selectable) — e.g. a
        column already mapped elsewhere.
  Behavior:
    - Clicking the button toggles dropdown visibility.
    - Clicking an item selects it and closes dropdown.
    - Outside clicks or ESC key close dropdown.
    - Keyboard navigation: ArrowDown/ArrowUp to cycle items, Enter to select, ESC to close.
  Edge Cases:
    - Empty items: shows placeholder message.
    - Long names: truncated with ellipsis + title attribute for tooltip.
    - Disabled: button visually muted & no interaction.
*/

const SelectDropdown = ({
  items = [],
  selectedId = null,
  onChange = () => {},
  placeholder = 'Select...',
  label,
  fullWidth = false,
  disabled = false,
  icon,
  size = 'medium',
  renderItem,
  renderTrigger,
  renderItemActions,
  renderGroup
}) => {
  const [open, setOpen] = useState(false);
  const buttonRef = useRef(null);
  const listRef = useRef(null);

  const selectedItem = items.find(i => i.id === selectedId) || null;

  const toggle = () => { if (!disabled) setOpen(o => !o); };

  const handleSelect = (item) => {
    onChange(item);
    setOpen(false);
    buttonRef.current?.focus();
  };

  const handleKeyDown = useCallback((e) => {
    if (!open) return;
    const focusable = listRef.current?.querySelectorAll('[data-dropdown-item]');
    if (!focusable || focusable.length === 0) return;
    const currentIndex = Array.from(focusable).findIndex(el => el === document.activeElement);

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        const next = currentIndex < focusable.length - 1 ? currentIndex + 1 : 0;
        focusable[next].focus();
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        const prev = currentIndex > 0 ? currentIndex - 1 : focusable.length - 1;
        focusable[prev].focus();
        break;
      }
      case 'Enter': {
        e.preventDefault();
        if (document.activeElement?.dataset?.id) {
          const item = items.find(i => i.id === document.activeElement.dataset.id);
          if (item) handleSelect(item);
        }
        break;
      }
      case 'Escape': {
        e.preventDefault();
        setOpen(false);
        buttonRef.current?.focus();
        break;
      }
      default:
        break;
    }
  }, [open, items]);

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!open) return;
      if (buttonRef.current && buttonRef.current.contains(e.target)) return;
      if (listRef.current && listRef.current.contains(e.target)) return;
      setOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [open]);

  // Manage initial focus inside list when opened
  useEffect(() => {
    if (open) {
      const first = listRef.current?.querySelector('[data-dropdown-item]');
      first?.focus();
    }
  }, [open]);

  return (
    <div className={`select-dropdown ${fullWidth ? 'full-width' : ''} size-${size} ${disabled ? 'disabled' : ''}`}>
      {label && <label className="select-dropdown-label">{label}</label>}
      <button
        ref={buttonRef}
        type="button"
        className="select-dropdown-trigger"
        onClick={toggle}
        disabled={disabled}
        aria-haspopup="listbox"
        aria-expanded={open}
      >
        {icon && <span className="select-dropdown-icon">{icon}</span>}
        <span className={`select-dropdown-value ${selectedItem ? 'has-value' : 'placeholder'}`}>
          {renderTrigger ? renderTrigger(selectedItem) : (selectedItem ? (selectedItem.name || '') : placeholder)}
        </span>
        <span className={`select-dropdown-arrow ${open ? 'open' : ''}`}>▼</span>
      </button>
      {open && (
        <ul 
          className="select-dropdown-list" 
          role="listbox"
          ref={listRef}
          onKeyDown={handleKeyDown}
        >
          {items.length === 0 && (
            <li className="select-dropdown-empty">No options</li>
          )}
          {items.map((item, idx) => {
            const actions = renderItemActions ? renderItemActions(item) : null;
            // Heading row wherever the group changes. Not an option: no
            // data-dropdown-item, so ArrowUp/Down and Enter never land on it.
            const group = item.group ?? null;
            const prevGroup = idx > 0 ? (items[idx - 1].group ?? null) : null;
            const heading = group !== null && group !== prevGroup ? (
              <li key={`group:${group}`} role="presentation" className="select-dropdown-group">
                {renderGroup
                  ? renderGroup(group, items.filter(i => (i.group ?? null) === group))
                  : <span className="select-dropdown-group-label">{group}</span>}
              </li>
            ) : null;
            return (
              <React.Fragment key={item.id}>
              {heading}
              <li className={`select-dropdown-row ${actions ? 'has-actions' : ''}`}>
                <button
                  type="button"
                  data-dropdown-item
                  data-id={item.id}
                  className={`select-dropdown-item ${item.id === selectedId ? 'active' : ''} ${item.muted ? 'muted' : ''}`}
                  onClick={() => handleSelect(item)}
                  title={item.name}
                  role="option"
                  aria-selected={item.id === selectedId}
                >
                  {renderItem ? (
                    renderItem(item, item.id === selectedId)
                  ) : item.content ? (
                    item.content
                  ) : (
                    <span className="item-label">{item.name}</span>
                  )}
                </button>
                {actions && (
                  // Close the list once an action fires — the action button is a
                  // sibling of the option button, so it never triggers selection.
                  <span
                    className="select-dropdown-item-actions"
                    onClick={() => setOpen(false)}
                  >
                    {actions}
                  </span>
                )}
              </li>
              </React.Fragment>
            );
          })}
        </ul>
      )}
    </div>
  );
};

export default SelectDropdown;
